import React from 'react';
import { Check, Minus } from 'lucide-react';
import Reveal from './Reveal';

/**
 * PackageComparison
 * Clean, premium side-by-side comparison of the three collections.
 * Uses only the finalised features - never invents anything.
 * Mobile: horizontal scroll with sticky first column, no page overflow.
 */

// Column configuration - Signature is highlighted as Most Popular
const COLUMNS = [
  { id: 'basic', name: 'Essential', price: 100, popular: false },
  { id: 'standard', name: 'Signature', price: 150, popular: true },
  { id: 'premium', name: 'Cinematic', price: 200, popular: false },
];

// Row configuration - matches the exact features in the pricing cards.
// value: string = show text; true = show check icon; false = show dash.
const ROWS = [
  {
    label: 'Photos included',
    values: { basic: 'Up to 20', standard: 'Up to 40', premium: 'Up to 60' },
  },
  {
    label: 'Video clips',
    values: { basic: 'Included', standard: 'Included', premium: 'More extensive integration' },
  },
  {
    label: 'Music tracks',
    values: { basic: '1', standard: '2', premium: '3' },
  },
  {
    label: 'Editing style',
    values: { basic: 'Cinematic editing', standard: 'Enhanced cinematic editing', premium: 'Cinematic editing + advanced effects' },
  },
  {
    label: 'Custom text overlays',
    values: { basic: false, standard: true, premium: true },
  },
  {
    label: 'Premium transitions',
    values: { basic: false, standard: true, premium: true },
  },
  {
    label: '15-second highlight reel',
    values: { basic: false, standard: false, premium: true },
  },
  {
    label: 'Free revisions',
    values: { basic: '1', standard: '2', premium: '3' },
  },
  {
    label: 'Production time (est.)',
    values: { basic: '1-3 days', standard: '2-4 days', premium: '3-5 days' },
  },
  {
    label: 'Digital + physical USB delivery',
    values: { basic: true, standard: true, premium: true },
  },
];

const Cell = ({ value }) => {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <div className="w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
          <Check size={14} className="text-amber-400" strokeWidth={3} />
        </div>
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center">
        <Minus size={16} className="text-gray-600" />
      </div>
    );
  }
  return <span className="text-gray-200 text-sm md:text-base">{value}</span>;
};

const PackageComparison = () => {
  return (
    <section className="mt-20 mb-16" data-testid="package-comparison">
      <Reveal className="text-center max-w-3xl mx-auto mb-10">
        <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Side-by-Side</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
          Compare the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Collections</span>
        </h2>
        <p className="text-gray-400 text-base md:text-lg">
          Every detail of what's included in each collection, at a glance.
        </p>
      </Reveal>

      <Reveal>
        {/* Horizontal scroll wrapper — prevents page overflow on mobile */}
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="package-comparison-scroll">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                {/* Sticky first column keeps feature labels visible when scrolling on mobile */}
                <th className="sticky left-0 z-10 bg-[#0a0a0a] text-left px-4 md:px-6 py-5 text-xs uppercase tracking-widest text-gray-500 font-medium">
                  Feature
                </th>
                {COLUMNS.map((col) => (
                  <th
                    key={col.id}
                    className={`px-4 md:px-6 py-5 text-center relative ${
                      col.popular ? 'bg-amber-500/5' : ''
                    }`}
                  >
                    {col.popular && (
                      <span className="absolute top-1 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-amber-400 font-semibold">
                        Most Popular
                      </span>
                    )}
                    <div className="text-white font-semibold text-base md:text-lg mt-2">{col.name}</div>
                    <div className="text-amber-400 font-bold text-xl md:text-2xl mt-1">£{col.price}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, idx) => (
                <tr
                  key={row.label}
                  className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                >
                  <td className="sticky left-0 z-10 bg-[#0a0a0a] px-4 md:px-6 py-4 text-gray-300 text-sm md:text-[15px] font-medium">
                    {row.label}
                  </td>
                  {COLUMNS.map((col) => (
                    <td
                      key={col.id}
                      className={`px-4 md:px-6 py-4 text-center align-middle ${
                        col.popular ? 'bg-amber-500/[0.03]' : ''
                      }`}
                    >
                      <Cell value={row.values[col.id]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile hint - only shown on small screens to indicate scroll */}
        <p className="md:hidden text-center text-xs text-gray-500 mt-3">
          Swipe to compare →
        </p>
      </Reveal>
    </section>
  );
};

export default PackageComparison;
